import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
  PayloadAction,
} from '@reduxjs/toolkit';

import {
  adminCreateLessonStudentFeedback,
  adminDeleteLessonStudentFeedback,
  adminGetLessonStudentFeedbackRewards,
  adminGetLessonStudentFeedbacks,
  adminUpdateLessonStudentFeedbacks,
} from './adminApi';
import { RootState } from '../..';
import { ISection } from '../SectionSlice';
import { ILessonHistory } from '../LessonHistorySlice';
import { IUser } from '../../UserSlice';
import {
  getPendingLessonStudentFeedback,
  createLessonStudentFeedback,
  getLessonStudentFeedbackRewards,
  getLessonStudentFeedbacks,
  updateLessonStudentFeedbacks,
} from './api';

export type TLessonStudentFeedbackRewardItem = {
  title: string;
  list: string[];
};

export interface ILessonStudentFeedbackRewardData {
  0: TLessonStudentFeedbackRewardItem;
  1: TLessonStudentFeedbackRewardItem;
  2: TLessonStudentFeedbackRewardItem;
  3: TLessonStudentFeedbackRewardItem;
  4: TLessonStudentFeedbackRewardItem;
  5: TLessonStudentFeedbackRewardItem;
}

export interface ILessonStudentFeedback {
  _id: string;
  student: string | IUser;
  tutor: string | IUser;
  section: string | ISection;
  lessonHistory: string | ILessonHistory;
  createdBy: string | IUser;
  lastUpdatedBy: string | IUser;
  createdAt: string;
  updatedAt: string;
  overallTutorRating: number;
  feedbackRewards?: string[];
  teachingConcept: number;
  questionPractice: number;
  communicationSkills: number;
  isContinueLesson: 'Yes' | 'No';
  subjectAndSyllabusKnowledge: number;
  punctualAndProfessional: number;
  technicalSkills: number;
  overallTutopiyaRating?: number;
  feedbackComments?: string;
  // new fields
  tutorExplanationClarity?: number;
  tutorSupportResponsiveness?: number;
  tutorAttitudeFriendliness?: number;
  classInteractionEngagement?: number;
  studentConfidenceImprovement?: number;
}

export interface IInitialLessonStudentFeedbacksState {
  status: 'idle' | 'loading' | 'rejected';
  lessonStudentFeedbackRewardsData?: ILessonStudentFeedbackRewardData;
  lessonStudentFeedbackListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ILessonStudentFeedback>;
  };
  pendingLessonStudentFeedback?: ILessonHistory;
  // Admin
  adminLessonStudentFeedbackRewardsData?: ILessonStudentFeedbackRewardData;
  adminLessonStudentFeedbackListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ILessonStudentFeedback>;
  };
}

const initialState: IInitialLessonStudentFeedbacksState = {
  status: 'idle',
  lessonStudentFeedbackRewardsData: undefined,
  lessonStudentFeedbackListData: undefined,
  // ADMIN
  adminLessonStudentFeedbackRewardsData: undefined,
  adminLessonStudentFeedbackListData: undefined,
};

export const LessonStudentFeedbacksSlice = createSlice({
  initialState,
  name: 'lessonStudentFeedbacks',
  reducers: {
    resetLessonStudentFeedbackRewardsData: (state) => {
      state.lessonStudentFeedbackRewardsData = undefined;
    },
    resetLessonStudentFeedbackListData: (state) => {
      state.lessonStudentFeedbackListData = undefined;
    },
    resetPendingLessonStudentFeedback: (state) => {
      state.pendingLessonStudentFeedback = undefined;
    },
    addNewStudentFeedbackData: (
      state,
      action: PayloadAction<ILessonStudentFeedback[]>
    ) => {
      const result = action.payload;

      if (state.lessonStudentFeedbackListData) {
        const newLessonStudentFeedbackListData = {
          ...state.lessonStudentFeedbackListData,
        };

        if (result && result.length) {
          result.forEach((feedbackItem) => {
            newLessonStudentFeedbackListData.list.unshift(feedbackItem);

            newLessonStudentFeedbackListData.totalDocs =
              (newLessonStudentFeedbackListData.totalDocs || 0) + 1;
          });
        }

        state.lessonStudentFeedbackListData = newLessonStudentFeedbackListData;
      }
    },
    updateStudentFeedbackData: (
      state,
      action: PayloadAction<{ id: string; feedback: ILessonStudentFeedback }>
    ) => {
      const result = action.payload;

      if (state.lessonStudentFeedbackListData) {
        const newLessonStudentFeedbackListData = {
          ...state.lessonStudentFeedbackListData,
        };

        if (
          newLessonStudentFeedbackListData.list &&
          newLessonStudentFeedbackListData.list.length
        ) {
          const index = newLessonStudentFeedbackListData.list.findIndex(
            (item) => item._id === result.id
          );

          if (index > -1) {
            newLessonStudentFeedbackListData.list[index] = result.feedback;
          }
        }

        state.lessonStudentFeedbackListData = newLessonStudentFeedbackListData;
      }
    },
    // Admin
    resetAdminLessonStudentFeedbackRewardsData: (state) => {
      state.adminLessonStudentFeedbackRewardsData = undefined;
    },
    resetAdminLessonStudentFeedbackListData: (state) => {
      state.adminLessonStudentFeedbackListData = undefined;
    },
    adminAddNewStudentFeedbackData: (
      state,
      action: PayloadAction<ILessonStudentFeedback[]>
    ) => {
      const result = action.payload;

      if (state.adminLessonStudentFeedbackListData) {
        const newAdminLessonStudentFeedbackListData = {
          ...state.adminLessonStudentFeedbackListData,
        };

        if (result && result.length) {
          result.forEach((feedbackItem) => {
            newAdminLessonStudentFeedbackListData.list.unshift(feedbackItem);

            newAdminLessonStudentFeedbackListData.totalDocs =
              (newAdminLessonStudentFeedbackListData.totalDocs || 0) + 1;
          });
        }

        state.adminLessonStudentFeedbackListData = newAdminLessonStudentFeedbackListData;
      }
    },
    adminUpdateStudentFeedbackData: (
      state,
      action: PayloadAction<{ id: string; feedback: ILessonStudentFeedback }>
    ) => {
      const result = action.payload;

      if (state.adminLessonStudentFeedbackListData) {
        const newAdminLessonStudentFeedbackListData = {
          ...state.adminLessonStudentFeedbackListData,
        };

        if (
          newAdminLessonStudentFeedbackListData.list &&
          newAdminLessonStudentFeedbackListData.list.length
        ) {
          const index = newAdminLessonStudentFeedbackListData.list.findIndex(
            (item) => item._id === result.id
          );

          if (index > -1) {
            newAdminLessonStudentFeedbackListData.list[index] = result.feedback;
          }
        }

        state.adminLessonStudentFeedbackListData = newAdminLessonStudentFeedbackListData;
      }
    },
    adminDeleteStudentFeedbackData: (
      state,
      action: PayloadAction<{ id: string }>
    ) => {
      const result = action.payload;

      if (state.adminLessonStudentFeedbackListData) {
        const newAdminLessonStudentFeedbackListData = {
          ...state.adminLessonStudentFeedbackListData,
        };

        if (
          newAdminLessonStudentFeedbackListData.list &&
          newAdminLessonStudentFeedbackListData.list.length
        ) {
          newAdminLessonStudentFeedbackListData.list = newAdminLessonStudentFeedbackListData.list.filter(
            (item) => item._id !== result.id
          );

          if (newAdminLessonStudentFeedbackListData.totalDocs) {
            newAdminLessonStudentFeedbackListData.totalDocs -= 1;
          }
        }

        state.adminLessonStudentFeedbackListData = newAdminLessonStudentFeedbackListData;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLessonStudentFeedbackRewards.fulfilled, (state, action) => {
        const result = action.payload;

        state.lessonStudentFeedbackRewardsData = result;
      })
      .addCase(getLessonStudentFeedbacks.fulfilled, (state, action) => {
        const result = action.payload;

        const newLessonStudentFeedbackListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.lessonStudentFeedbackListData = newLessonStudentFeedbackListData;
      })
      .addCase(getPendingLessonStudentFeedback.fulfilled, (state, action) => {
        const result = action.payload;

        state.pendingLessonStudentFeedback = result;
      });

    // Admin
    builder
      .addCase(
        adminGetLessonStudentFeedbackRewards.fulfilled,
        (state, action) => {
          const result = action.payload;

          state.adminLessonStudentFeedbackRewardsData = result;
        }
      )
      .addCase(adminGetLessonStudentFeedbacks.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminLessonStudentFeedbackListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminLessonStudentFeedbackListData = newAdminLessonStudentFeedbackListData;
      });

    // Common Matchers
    builder.addMatcher(
      isFulfilled(
        getLessonStudentFeedbackRewards,
        createLessonStudentFeedback,
        updateLessonStudentFeedbacks,
        // Admin
        adminGetLessonStudentFeedbackRewards,
        adminCreateLessonStudentFeedback,
        adminUpdateLessonStudentFeedbacks,
        adminDeleteLessonStudentFeedback,
        getLessonStudentFeedbacks
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isPending(
        getLessonStudentFeedbackRewards,
        createLessonStudentFeedback,
        updateLessonStudentFeedbacks,
        // Admin
        adminGetLessonStudentFeedbackRewards,
        adminCreateLessonStudentFeedback,
        adminUpdateLessonStudentFeedbacks,
        adminDeleteLessonStudentFeedback,
        getLessonStudentFeedbacks
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        getLessonStudentFeedbackRewards,
        createLessonStudentFeedback,
        getLessonStudentFeedbacks,
        updateLessonStudentFeedbacks,
        // Admin
        adminGetLessonStudentFeedbackRewards,
        adminCreateLessonStudentFeedback,
        adminUpdateLessonStudentFeedbacks,
        adminDeleteLessonStudentFeedback
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getLessonStudentFeedbacksState = (
  state: RootState
): RootState['lessonStudentFeedbacks'] => state.lessonStudentFeedbacks;

export const {
  resetLessonStudentFeedbackRewardsData,
  resetLessonStudentFeedbackListData,
  addNewStudentFeedbackData,
  updateStudentFeedbackData,
  resetPendingLessonStudentFeedback,
  // Admin
  resetAdminLessonStudentFeedbackRewardsData,
  resetAdminLessonStudentFeedbackListData,
  adminAddNewStudentFeedbackData,
  adminUpdateStudentFeedbackData,
  adminDeleteStudentFeedbackData,
} = LessonStudentFeedbacksSlice.actions;

export * from './adminApi';
export * from './api';
export default LessonStudentFeedbacksSlice.reducer;
